import generateLabel from "config/helpers/syncLabel";
import i18n from "multilingual";

// Function to get subtask labels with translation function
const getSubtaskLabels = (t) => ({
    "ehr.sync.active-appointment": t ? t("tasks.APPOINTMENTS") : i18n.t("tasks.APPOINTMENTS"),
    "ehr.sync.patient": t ? t("tasks.PATIENTS") : i18n.t("tasks.PATIENTS"),
    "ehr.sync.patient-demographics": t ? t("tasks.PATIENT_DEMOGRAPHICS") : i18n.t("tasks.PATIENT_DEMOGRAPHICS"),
});

export const getSubtaskLabel = (code) => {
  switch (code) {
    case "ehr.sync.active-appointment":
      return i18n.t("sync.ACTIVE_APPOINTMENT");
    case "ehr.sync.patient":
      return i18n.t("sync.PATIENT");
    case "ehr.sync.patient-demographics":
      return i18n.t("sync.PATIENT_DEMOGRAPHICS");
    default:
      return null;
  }
};

/**
 * Calculates progress percentage from progressStats object
 * @param {Object} progressStats
 * @returns {number} 0-100
 */
export const calculateProgress = (progressStats) => {
    if (!progressStats) return 0;
    const { totalResources = 0, processedResources = 0 } = progressStats;
    return totalResources > 0
        ? Math.round((processedResources / totalResources) * 100)
        : 0;
};

/**
 * Extracts progress and task data from a raw FHIR Task resource
 * @param {Object} fhir Raw FHIR resource
 * @returns {Object} { progress, taskData, status }
 */
export const extractProgressFromFhir = (fhir) => {
    try {
        const entry = fhir?.entry?.[0];
        const resource = entry?.resource;
        const outputStr = resource?.output?.[0]?.valueString;

        if (!outputStr) {
            return {
                progress: 0,
                taskData: null,
                status: resource?.status || 'PENDING'
            };
        }

        const data = JSON.parse(outputStr);
        return {
            progress: typeof data.progress === "number" ? Math.round(data.progress) : 0,
            taskData: data,
            status: data.status || resource?.status || "RUNNING",
        };
    } catch (e) {
        return { progress: 0, taskData: null, status: "PENDING" };
    }
};

/**
 * Parses raw task data into UI-friendly groups and tasks
 * @param {Object} rawData The parsed task data (from output string)
 * @param {number} overallProgress Overall task progress
 * @param {Function} t Translation function (optional)
 * @returns {Array} Array of processed groups
 */
export const extractProcessedGroups = (rawData, overallProgress = 0, t = null) => {
    if (!rawData) return [];

    const rawGroups = Array.isArray(rawData.taskGroups) ? rawData.taskGroups : [];
    const isOverallCompleted = rawData.status === "COMPLETED" || overallProgress === 100;

    return rawGroups.map((group, groupIndex) => {
        const groupCode = group?.code || `group-${groupIndex}`;
        const groupLabel = generateLabel(groupCode, t);
        let groupStatus = group?.status || "PENDING";

        if (isOverallCompleted && groupStatus !== "FAILED") {
            groupStatus = "COMPLETED";
        }

        let groupProgress = 0;

        // Calculate Group Progress
        if (groupStatus === "COMPLETED") {
            groupProgress = 100;
        } else if (group?.tasks && group.tasks.length > 0) {
            const anyPending = group.tasks.some(
                (t) => (t?.status || "PENDING") === "PENDING"
            );

            if (anyPending) {
                groupProgress = 0;
            } else {
                const values = group.tasks
                    .map((t) => {
                        if (!t) return null;
                        if (t.progressStats && typeof t.progressStats.progress === "number")
                            return Math.round(t.progressStats.progress);
                        if (typeof t.progress === "number") return Math.round(t.progress);
                        if (
                            t.progressStats &&
                            (typeof t.progressStats.totalResources === "number" ||
                                typeof t.progressStats.processedResources === "number")
                        )
                            return calculateProgress(t.progressStats);
                        if (t.status === "COMPLETED") return 100;
                        return null;
                    })
                    .filter((v) => v !== null);

                if (values.length > 0) {
                    groupProgress = Math.round(
                        values.reduce((s, v) => s + v, 0) / values.length
                    );
                } else {
                    groupProgress = typeof rawData.progress === "number"
                        ? Math.round(rawData.progress)
                        : overallProgress;
                }
            }
        } else {
            groupProgress = typeof rawData.progress === "number"
                ? Math.round(rawData.progress)
                : overallProgress;
        }

        // Process Tasks within Group
        const tasksProcessed = Array.isArray(group.tasks)
            ? group.tasks.map((task, ti) => {
                const taskCode = task?.code || `task-${ti}`;
                const taskLabel = generateLabel(taskCode, t);
                let taskStatus = task?.status || "PENDING";

                if (isOverallCompleted && taskStatus !== "FAILED") {
                    taskStatus = "COMPLETED";
                }

                let taskProgress = 0;
                const stats = task?.progressStats || {};
                const explicitProgress = typeof stats.progress === "number"
                    ? Math.round(stats.progress)
                    : null;

                if (explicitProgress !== null) {
                    taskProgress = explicitProgress;
                } else if (typeof task.progress === "number") {
                    taskProgress = Math.round(task.progress);
                } else if (taskStatus === "COMPLETED") {
                    taskProgress = 100;
                } else if (taskStatus === "RUNNING") {
                    taskProgress = groupProgress;
                }

                const currentSubTask = stats.currentSubTask || null;
                const subtaskLabels = getSubtaskLabels(t);
                const friendlySubtask = currentSubTask
                    ? subtaskLabels[currentSubTask] || generateLabel(currentSubTask, t)
                    : null;

                return {
                    code: taskCode,
                    label: taskLabel,
                    status: taskStatus,
                    progress: taskProgress,
                    progressStats: stats,
                    currentSubTask,
                    friendlySubtask,
                    error: task?.error || null,
                };
            }).filter(Boolean)
            : [];

        return {
            code: groupCode,
            label: groupLabel,
            status: groupStatus,
            progress: groupProgress,
            tasks: tasksProcessed,
            disableMovement: groupCode.includes("priority-sync"),
            isCurrent: groupCode === rawData?.currentTaskGroupCode,
        };
    });
};
